/**
 * Instant-replay "REPLAY" chip — a small always-on indicator shown for the
 * duration of game/replay.ts's scripted goal playback. Same discipline as
 * ui/banners.ts: plain DOM, its own tiny bit of state, no game-loop drawing
 * logic in here — the mount just flips `setVisible` once per state change.
 */

let styleInjected = false;

function ensureStyle(): void {
  if (styleInjected) return;
  styleInjected = true;
  const style = document.createElement('style');
  style.textContent = CSS;
  document.head.appendChild(style);
}

export interface ReplayChip {
  setVisible(visible: boolean): void;
  dispose(): void;
}

export function mountReplayChip(container: HTMLElement): ReplayChip {
  ensureStyle();
  const el = document.createElement('div');
  el.className = 'fgreplay';
  el.innerHTML = `<span class="fgreplay-dot"></span><span class="fgreplay-label">REPLAY</span>`;
  container.appendChild(el);

  return {
    setVisible(visible: boolean): void {
      el.classList.toggle('fgreplay-show', visible);
    },
    dispose(): void {
      el.remove();
    },
  };
}

/**
 * Center-top-left chip, deliberately understated next to banners.ts's big
 * event banners — a broadcast "REPLAY" bug, not another headline. Skew
 * matches the rest of the broadcast package (hud.ts / banners.ts).
 */
const CSS = `
.fgreplay {
  position: fixed; top: 12vh; left: 24px;
  z-index: 19; pointer-events: none;
  display: flex; align-items: center; gap: 8px;
  padding: 7px 16px;
  background: linear-gradient(180deg, rgba(32,37,50,0.97) 0%, rgba(14,17,25,0.95) 100%);
  border-left: 4px solid #ff4a4a;
  border-radius: 3px;
  transform: skewX(-10deg) translateY(-14px);
  opacity: 0;
  box-shadow: 0 6px 24px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08);
  transition: opacity 180ms ease, transform 180ms ease;
  font-family: 'Segoe UI', 'Helvetica Neue', system-ui, sans-serif;
}
.fgreplay-show { opacity: 1; transform: skewX(-10deg) translateY(0); }
.fgreplay-dot {
  width: 9px; height: 9px; border-radius: 50%; background: #ff4a4a;
  box-shadow: 0 0 8px 2px rgba(255,74,74,0.7);
  animation: fgreplayPulse 1s ease-in-out infinite;
  transform: skewX(10deg);
}
.fgreplay-label {
  color: #fff; font-weight: 900; letter-spacing: 3px; font-size: 13px;
  text-shadow: 0 1px 2px rgba(0,0,0,0.6);
  transform: skewX(10deg);
}
@keyframes fgreplayPulse { 50% { opacity: 0.25; } }
`;
